import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { userService } from '../services/api';
import { User } from '../types';
import { toast } from 'react-toastify';
import EmailLink from '../components/EmailLink';
import PhoneLink from '../components/PhoneLink';
import UserPhotoCell from '../components/UserPhotoCell';
import './Users.css';

type ModalKind = 'none' | 'create' | 'edit' | 'password';

/** Must match hostel-be/app/config/primarySuperAdmin.js (seed primary). */
const PRIMARY_SEED_PHONE = '7416003526';
const PRIMARY_SEED_EMAIL = 'super@loftinnpg.com';

function isPrimarySeeded(u: User) {
  if (u.isPrimarySuperAdmin === true) return true;
  if (u.userType !== 'SUPER_ADMIN') return false;
  return (
    u.phoneNumber === PRIMARY_SEED_PHONE ||
    (u.email || '').toLowerCase() === PRIMARY_SEED_EMAIL.toLowerCase()
  );
}

const Users: React.FC = () => {
  const { user: authUser } = useAuth();
  const myId = authUser?.id;
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState<boolean | null>(null);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [modal, setModal] = useState<ModalKind>('none');
  const [selected, setSelected] = useState<User | null>(null);
  const [busy, setBusy] = useState(false);

  const [cUsername, setCUsername] = useState('');
  const [cPhone, setCPhone] = useState('');
  const [cEmail, setCEmail] = useState('');
  const [cPassword, setCPassword] = useState('');

  const [eUsername, setEUsername] = useState('');
  const [ePhone, setEPhone] = useState('');
  const [eEmail, setEEmail] = useState('');

  const [p1, setP1] = useState('');
  const [p2, setP2] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await userService.getAllUsers({ includeDeleted });
      setUsers(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load super admin accounts');
    } finally {
      setLoading(false);
    }
  }, [includeDeleted]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const closeModals = () => {
    setModal('none');
    setSelected(null);
    setCUsername('');
    setCPhone('');
    setCEmail('');
    setCPassword('');
    setEUsername('');
    setEPhone('');
    setEEmail('');
    setP1('');
    setP2('');
  };

  const openCreate = () => {
    closeModals();
    setModal('create');
  };

  const openEdit = (u: User) => {
    setSelected(u);
    setEUsername(u.username || '');
    setEPhone(u.phoneNumber || '');
    setEEmail(u.email || '');
    setModal('edit');
  };

  const openPassword = (u: User) => {
    setSelected(u);
    setP1('');
    setP2('');
    setModal('password');
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cUsername.trim() || !cPhone.trim() || !cPassword) {
      toast.error('Name, phone, and password are required');
      return;
    }
    if (cPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setBusy(true);
    try {
      await userService.createSuperAdminAccount({
        username: cUsername.trim(),
        phoneNumber: cPhone.trim(),
        password: cPassword,
        email: cEmail.trim() || undefined,
      });
      toast.success('Super admin created');
      closeModals();
      loadUsers();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    setBusy(true);
    try {
      await userService.updateSuperAdminAccount(selected.id, {
        username: eUsername.trim(),
        phoneNumber: ePhone.trim(),
        email: eEmail.trim() === '' ? null : eEmail.trim(),
      });
      toast.success('Updated');
      closeModals();
      loadUsers();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const handlePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    if (p1.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (p1 !== p2) {
      toast.error('Passwords do not match');
      return;
    }
    setBusy(true);
    try {
      await userService.changePassword(selected.id, p1);
      toast.success('Password updated');
      closeModals();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Password update failed');
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (u: User) => {
    if (isPrimarySeeded(u) && u.isActive) {
      toast.error('The primary platform super admin cannot be deactivated');
      return;
    }
    if (myId === u.id && u.isActive) {
      toast.error('You cannot deactivate your own account');
      return;
    }
    setBusy(true);
    try {
      await userService.setSuperAdminActive(u.id, !u.isActive);
      toast.success(u.isActive ? 'Deactivated' : 'Activated');
      loadUsers();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Status update failed');
    } finally {
      setBusy(false);
    }
  };

  const softDelete = async (u: User) => {
    if (isPrimarySeeded(u)) {
      toast.error('The primary platform super admin cannot be removed');
      return;
    }
    if (myId === u.id) {
      toast.error('You cannot delete your own account');
      return;
    }
    if (!window.confirm(`Remove super admin access for ${u.username || u.phoneNumber}?`)) return;
    setBusy(true);
    try {
      await userService.deleteSuperAdminAccount(u.id);
      toast.success('Account removed');
      loadUsers();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const restoreUser = async (u: User) => {
    setBusy(true);
    try {
      await userService.restoreSuperAdminAccount(u.id);
      toast.success('Account restored');
      loadUsers();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Restore failed');
    } finally {
      setBusy(false);
    }
  };

  const filteredUsers = users.filter((u) => {
    const matchesSearch =
      u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.phoneNumber?.includes(searchTerm) ||
      (u.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterActive === null || u.isActive === filterActive;
    return matchesSearch && matchesFilter;
  });

  if (loading && users.length === 0) {
    return <div className="loading">Loading super admin accounts…</div>;
  }

  return (
    <div className="users-page">
      <div className="page-header">
        <div>
          <h1>Super admin accounts</h1>
          <p className="users-page-lead">Platform-level administrators only. Tenants and hostel staff are managed per hostel.</p>
        </div>
        <div className="filters sa-users-toolbar">
          <button type="button" className="view-btn" onClick={openCreate} disabled={busy}>
            + Create super admin
          </button>
          <label className="sa-checkbox">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
            />
            Show removed
          </label>
          <input
            type="text"
            placeholder="Search name, phone, email…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <select
            value={filterActive === null ? 'all' : filterActive.toString()}
            onChange={(e) => {
              const value = e.target.value;
              setFilterActive(value === 'all' ? null : value === 'true');
            }}
            className="filter-select"
          >
            <option value="all">All</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
      </div>

      <div className="users-table-container responsive-table-wrap">
        <table className="users-table responsive-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Status</th>
              <th>Removed</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr className="responsive-table__empty">
                <td colSpan={7} className="no-data" data-label="">
                  No super admin accounts match your filters.
                </td>
              </tr>
            ) : (
              filteredUsers.map((u) => (
                <tr key={u.id}>
                  <td data-label="ID">
                    #{u.id}
                    {isPrimarySeeded(u) && <span className="sa-primary-badge">Primary</span>}
                  </td>
                  <td className="responsive-table__primary" data-label="Name">
                    <div className="user-list-name-cell">
                      <UserPhotoCell name={u.username} photoUrl={u.photo} size="sm" />
                      <span title={u.username}>{u.username || '—'}</span>
                    </div>
                  </td>
                  <td data-label="Phone">
                    <PhoneLink phone={u.phoneNumber} />
                  </td>
                  <td data-label="Email">
                    <EmailLink email={u.email} />
                  </td>
                  <td data-label="Status">
                    <span className={`status-badge ${u.isActive ? 'active' : 'inactive'}`}>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td data-label="Removed">{u.isDeleted ? <span className="status-badge inactive">Yes</span> : '—'}</td>
                  <td className="responsive-table__actions" data-label="Actions">
                    <div className="sa-action-cell">
                      {u.isDeleted ? (
                        <button type="button" className="view-btn sa-btn-sm" disabled={busy} onClick={() => restoreUser(u)}>
                          Restore
                        </button>
                      ) : (
                        <>
                          <button type="button" className="view-btn sa-btn-sm" disabled={busy} onClick={() => openEdit(u)}>
                            Edit
                          </button>
                          <button type="button" className="view-btn sa-btn-sm" disabled={busy} onClick={() => openPassword(u)}>
                            Password
                          </button>
                          <button
                            type="button"
                            className="view-btn sa-btn-sm sa-btn-muted"
                            disabled={busy || myId === u.id || (isPrimarySeeded(u) && u.isActive)}
                            title={isPrimarySeeded(u) && u.isActive ? 'Primary seed account cannot be deactivated' : undefined}
                            onClick={() => toggleActive(u)}
                          >
                            {u.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            type="button"
                            className="view-btn sa-btn-sm sa-btn-danger"
                            disabled={busy || myId === u.id || isPrimarySeeded(u)}
                            title={isPrimarySeeded(u) ? 'Primary seed account cannot be removed' : undefined}
                            onClick={() => softDelete(u)}
                          >
                            Remove
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="users-summary">
        <div className="summary-item">
          <span className="label">Listed</span>
          <span className="value">{filteredUsers.length}</span>
        </div>
        <div className="summary-item">
          <span className="label">Active</span>
          <span className="value">{filteredUsers.filter((u) => u.isActive && !u.isDeleted).length}</span>
        </div>
      </div>

      {modal !== 'none' && (
        <div className="sa-modal-backdrop" role="presentation" onClick={() => !busy && closeModals()}>
          <div className="sa-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            {modal === 'create' && (
              <form onSubmit={handleCreate}>
                <h2 className="sa-modal-title">Create super admin</h2>
                <div className="sa-field">
                  <label>Name</label>
                  <input value={cUsername} onChange={(e) => setCUsername(e.target.value)} required />
                </div>
                <div className="sa-field">
                  <label>Phone</label>
                  <input value={cPhone} onChange={(e) => setCPhone(e.target.value)} required />
                </div>
                <div className="sa-field">
                  <label>Email (optional)</label>
                  <input type="email" value={cEmail} onChange={(e) => setCEmail(e.target.value)} />
                </div>
                <div className="sa-field">
                  <label>Password</label>
                  <input type="password" value={cPassword} onChange={(e) => setCPassword(e.target.value)} required />
                </div>
                <div className="sa-modal-actions">
                  <button type="button" className="sa-btn-cancel" disabled={busy} onClick={closeModals}>
                    Cancel
                  </button>
                  <button type="submit" className="view-btn" disabled={busy}>
                    {busy ? 'Saving…' : 'Create'}
                  </button>
                </div>
              </form>
            )}

            {modal === 'edit' && selected && (
              <form onSubmit={handleEdit}>
                <h2 className="sa-modal-title">Edit #{selected.id}</h2>
                <div className="sa-field">
                  <label>Name</label>
                  <input value={eUsername} onChange={(e) => setEUsername(e.target.value)} required />
                </div>
                <div className="sa-field">
                  <label>Phone</label>
                  <input value={ePhone} onChange={(e) => setEPhone(e.target.value)} required />
                </div>
                <div className="sa-field">
                  <label>Email</label>
                  <input type="email" value={eEmail} onChange={(e) => setEEmail(e.target.value)} />
                </div>
                <div className="sa-modal-actions">
                  <button type="button" className="sa-btn-cancel" disabled={busy} onClick={closeModals}>
                    Cancel
                  </button>
                  <button type="submit" className="view-btn" disabled={busy}>
                    {busy ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </form>
            )}

            {modal === 'password' && selected && (
              <form onSubmit={handlePassword}>
                <h2 className="sa-modal-title">New password for #{selected.id}</h2>
                <p className="sa-modal-hint">
                  {selected.username || <PhoneLink phone={selected.phoneNumber} />}
                </p>
                <div className="sa-field">
                  <label>New password</label>
                  <input type="password" value={p1} onChange={(e) => setP1(e.target.value)} required minLength={6} />
                </div>
                <div className="sa-field">
                  <label>Confirm</label>
                  <input type="password" value={p2} onChange={(e) => setP2(e.target.value)} required minLength={6} />
                </div>
                <div className="sa-modal-actions">
                  <button type="button" className="sa-btn-cancel" disabled={busy} onClick={closeModals}>
                    Cancel
                  </button>
                  <button type="submit" className="view-btn" disabled={busy}>
                    {busy ? 'Updating…' : 'Update password'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
